import React from "react";
import {
  Box,
  Grid,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { StandardCard } from "../../../components/Cards/StandardCard";
import { IActionItem } from "../../../redux/actionItems";

export interface IChatLeadActivityDetailsProps {
  actionItem: IActionItem;
  onClose?: () => void;
}

const formatDisplay = (value: any): string => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const formatDateDisplay = (value: any): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const toLabel = (key: string) =>
  key
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <Box sx={{ mt: 1, mb: 0.5 }}>
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        textTransform: "uppercase",
        color: "primary.main",
        letterSpacing: 0.5,
        fontSize: "0.75rem",
      }}
    >
      {title}
    </Typography>
    <Divider sx={{ mt: 0.5, mb: 1.5 }} />
  </Box>
);

const ReadOnlyField: React.FC<{
  label: string;
  value?: React.ReactNode;
  multiline?: boolean;
}> = ({ label, value, multiline }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: multiline ? "column" : "row",
      alignItems: multiline ? "flex-start" : "center",
      justifyContent: "space-between",
      width: "100%",
      py: 0.75,
      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
    }}
  >
    <Typography
      variant="body2"
      sx={{
        color: "text.secondary",
        fontWeight: 600,
        fontSize: "0.8rem",
        minWidth: multiline ? "auto" : "110px",
        pr: 1,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        color: value ? "text.primary" : "text.disabled",
        whiteSpace: multiline ? "pre-wrap" : "normal",
        wordBreak: "break-word",
        textAlign: multiline ? "left" : "right",
        mt: multiline ? 0.5 : 0,
        flex: 1,
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

export const ChatLeadActivityDetails: React.FC<IChatLeadActivityDetailsProps> = ({
  actionItem,
  onClose,
}) => {
  const extras: Record<string, any> | null = React.useMemo(() => {
    const ex = (actionItem as any)?.extras;
    if (!ex || typeof ex !== "object" || Array.isArray(ex)) return null;
    return ex as Record<string, any>;
  }, [actionItem]);

  const extrasEntries = React.useMemo(() => {
    if (!extras) return [];
    return Object.entries(extras)
      .filter(([k, v]) => k && v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
  }, [extras]);

  return (
    <StandardCard
      heading="Action Item Details"
      rightHeading={
        onClose ? (
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        ) : undefined
      }
      sx={{
        width: "100%",
        height: "100%",
        maxHeight: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          pr: 1,
        }}
      >
        {/* ===== ACTION ITEM DETAILS ===== */}
        <SectionHeader title="Action Item Details" />
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <ReadOnlyField label="Action Item" value={actionItem.item} />
          </Grid>
          {actionItem.recommendation && (
            <Grid item xs={12}>
              <ReadOnlyField
                label="Recommendation"
                value={actionItem.recommendation}
                multiline
              />
            </Grid>
          )}
          {actionItem.description && (
            <Grid item xs={12}>
              <ReadOnlyField
                label="Description"
                value={actionItem.description}
                multiline
              />
            </Grid>
          )}
        </Grid>

        {/* ===== EVENT & BOOKING ===== */}
        <SectionHeader title="Event & Booking" />
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <ReadOnlyField
              label="Event Name"
              value={actionItem.event_type || actionItem.other_event_name || ""}
            />
          </Grid>
          <Grid item xs={12}>
            <ReadOnlyField
              label="Event Time"
              value={actionItem.event_time || ""}
            />
          </Grid>
          <Grid item xs={12}>
            <ReadOnlyField
              label="Preferred Venue"
              value={actionItem.preferred_venue || ""}
            />
          </Grid>
          <Grid item xs={12}>
            <ReadOnlyField
              label="Date of Visit"
              value={formatDateDisplay(actionItem.date_of_visit)}
            />
          </Grid>
          <Grid item xs={12}>
            <ReadOnlyField
              label="Function Date"
              value={formatDateDisplay(actionItem.function_date)}
            />
          </Grid>
          <Grid item xs={12}>
            <ReadOnlyField
              label="Booking Date"
              value={formatDateDisplay(actionItem.booking_date)}
            />
          </Grid>
          <Grid item xs={12}>
            <ReadOnlyField
              label="Function Month / Year"
              value={`${actionItem.function_month || "—"} / ${actionItem.function_year || "—"}`}
            />
          </Grid>
          <Grid item xs={12}>
            <ReadOnlyField
              label="Rooms"
              value={formatDisplay(actionItem.number_of_rooms)}
            />
          </Grid>
          <Grid item xs={12}>
            <ReadOnlyField
              label="Guests"
              value={formatDisplay(actionItem.number_of_guests)}
            />
          </Grid>
          <Grid item xs={12}>
            <ReadOnlyField
              label="Estimated Rough Revenue"
              value={formatDisplay(actionItem.estimated_rough_revenue)}
            />
          </Grid>
          <Grid item xs={12}>
            <ReadOnlyField
              label="Guest Budget"
              value={formatDisplay(actionItem.guest_budget)}
            />
          </Grid>
        </Grid>

        {/* ===== ADDITIONAL DETAILS (EXTRAS) ===== */}
        {extrasEntries.length > 0 && (
          <>
            <SectionHeader title="Extras" />
            <Grid container spacing={1.5}>
              {extrasEntries.map(([key, value]) => (
                <Grid item xs={12} key={key}>
                  <ReadOnlyField
                    label={toLabel(key)}
                    value={formatDisplay(value)}
                    multiline={String(value || "").length > 60}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </StandardCard>
  );
};
